import { Button, DatePicker, Dropdown, InputField, LabelComponent, Stack } from 'components';
import { ErrorContainer } from 'components/errorContainer';
import AccordionComponent from 'components/accordion/accordion';
import { FieldArray, useFormikContext } from 'formik';
import ZCodesSelect from 'modules/notes/notePopup/noteEdit/zCodesSelect/zCodesSelect';
import { clearZCodes } from 'store/clientProfile/clientProfileSlice';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { useParams } from 'react-router-dom';
import { useCallback, useEffect, useRef, useState } from 'react';
import debounce from 'lodash.debounce';
import { getBillingProviders, getZCodes } from 'store/clientProfile/clientProfileActions';
import clsx from 'clsx';
import { useResponsive } from 'utils/hooks/useMediaQuery';
import { getBillingPrograms } from 'store/clientProfile/clientProfileActions';
import { getLsUserRoleObject } from 'modules/networkTools/localStorageTokens';

import SectionTitle from '../sectionTitle/sectionTitle';
import Branch from './branch/branch';
import { FormValues } from '../../newClientForm';
import style from './billing.module.scss';
import { isSectionInvalid } from '../personalDetails/personalDetails';

const getLabel = (index: number) => {
  if (index === 0) {
    return 'Primary Z Code';
  }
  if (index === 1) {
    return 'Secondary Z Code';
  }
  return `Z Code ${index + 1}`;
};

const ZCodesField = ({ zCode, index }: { zCode: { id: string; name: string }; index: number }) => {
  const formik = useFormikContext<FormValues>();
  const dispatch = useAppDispatch();
  const { clientId = '' } = useParams();
  const [searchInputValue, setSearchInputValue] = useState('');
  const zCodes = useAppSelector((state) => state.clientProfile.zCodes.data);
  const pendingGetZCodesRef = useRef<{ abort: () => void } | null>(null);

  const mappedZCodes = zCodes.map((z) => ({ id: z.id, name: `${z.code} - ${z.description}` }));

  const data = searchInputValue ? mappedZCodes : [];

  const debouncedSearch = useCallback(
    debounce(
      (
        query: string,
        _pendingGetZCodesRef: React.MutableRefObject<{ abort: () => void } | null>
      ) => {
        if (query) {
          const promise = dispatch(getZCodes({ query }));
          _pendingGetZCodesRef.current = promise;
        }
      },
      200
    ),
    [clientId, dispatch]
  );

  const onSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchInputValue(value);
    pendingGetZCodesRef.current?.abort();
    if (value === '') {
      dispatch(clearZCodes());
    } else {
      debouncedSearch(value, pendingGetZCodesRef);
    }
  };

  return (
    <Stack width="100%" mb="16px">
      <Stack display="flex" flexDirection="row" alignItems="center" gap="4px">
        <LabelComponent text={getLabel(index)} />
      </Stack>
      <ZCodesSelect
        withFavorites={false}
        multiple={false}
        showTitle={searchInputValue !== '' ? false : true}
        data={data.map((z) => ({ id: z.id, name: z.name, info: z.name }))}
        value={zCode}
        onClose={() => {
          setSearchInputValue('');
          dispatch(clearZCodes());
        }}
        searchInputValue={searchInputValue}
        onSearchInputChange={(e) => onSearchInputChange(e)}
        onChange={(e) => {
          const newValue = zCodes.find((z) => z.id?.toString() === e.target.value);
          formik.setFieldValue(`billing.zCodes.${index}`, newValue);
        }}
        label="Select"
      />
    </Stack>
  );
};

const OrderingProviderSection = () => {
  const dispatch = useAppDispatch();
  const { isTablet } = useResponsive();
  const formik = useFormikContext<FormValues>();
  const { organizationId } = getLsUserRoleObject();
  const billingProviders = useAppSelector(
    (state) => state.clientProfile.billingProviders.data
  ).filter((p) => p.id !== -1);

  useEffect(() => {
    dispatch(getBillingProviders({ organizationId }));
  }, [organizationId]);

  const hasBillingProviders = billingProviders.length > 0;

  return (
    <Stack width="100%" display="flex" flexDirection="column" gap="8px">
      <FieldArray name="billing.orderingProviders">
        {({ remove, push }) => (
          <>
            {formik.values?.billing?.orderingProviders?.map?.((orderingProvider, index) => {
              const errors = formik.errors.billing?.orderingProviders?.[index];
              if (typeof errors === 'string') {
                return null;
              }
              return (
                <Stack
                  width="100%"
                  display="flex"
                  flexDirection={isTablet ? 'column' : 'row'}
                  alignItems={isTablet ? 'flex-start' : 'center'}
                  gap={isTablet ? '0px' : '24px'}
                  key={index}
                  className={style.rowWrapper}
                >
                  <Stack
                    flex={3}
                    width="100%"
                    display="flex"
                    flexDirection="row"
                    alignItems="center"
                    gap="4px"
                  >
                    <Stack flex={index === 0 ? undefined : 9} width={index === 0 ? '100%' : 'auto'}>
                      <LabelComponent text="Ordering Provider" />
                      <Dropdown
                        name={`billing.orderingProviders.${index}.orderingProviderId`}
                        value={formik.values.billing?.orderingProviders?.[
                          index
                        ]?.orderingProviderId?.toString()}
                        onChange={formik.handleChange}
                        data={billingProviders}
                        label="Select"
                        disabled={!hasBillingProviders}
                      />
                      <ErrorContainer
                        visible={
                          formik.touched.billing?.orderingProviders?.[index]?.orderingProviderId &&
                          errors?.orderingProviderId
                        }
                        errors={formik.errors}
                        name={`billing.orderingProviders.${index}.orderingProviderId`}
                      />
                    </Stack>
                    {isTablet && (
                      <Stack flex={1}>
                        {index !== 0 && (
                          <button onClick={() => remove(index)}>
                            <span className={clsx('material-icons', style.deleteIcon)}>delete</span>
                          </button>
                        )}
                      </Stack>
                    )}
                  </Stack>
                  <Stack flex={3} width="100%">
                    <LabelComponent text="Start Date" />
                    <DatePicker
                      disabled={!hasBillingProviders}
                      value={orderingProvider.startDate}
                      onChange={(value: string) => {
                        formik
                          .setFieldValue(`billing.orderingProviders.${index}.startDate`, value)
                          .then(() => {
                            formik.setFieldTouched(
                              `billing.orderingProviders.${index}.startDate`,
                              true
                            );
                          });
                      }}
                      onBlur={() =>
                        formik.setFieldTouched(`billing.orderingProviders.${index}.startDate`, true)
                      }
                    />
                    <ErrorContainer
                      visible={
                        formik.touched.billing?.orderingProviders?.[index]?.startDate &&
                        errors?.startDate
                      }
                      errors={formik.errors}
                      name={`billing.orderingProviders.${index}.startDate`}
                    />
                  </Stack>
                  <Stack flex={3} width="100%">
                    <LabelComponent text="End Date" />
                    <DatePicker
                      disabled={!hasBillingProviders}
                      value={orderingProvider.endDate}
                      onChange={(value: string) => {
                        formik
                          .setFieldValue(`billing.orderingProviders.${index}.endDate`, value)
                          .then(() => {
                            formik.setFieldTouched(
                              `billing.orderingProviders.${index}.endDate`,
                              true
                            );
                          });
                      }}
                      onBlur={() =>
                        formik.setFieldTouched(`billing.orderingProviders.${index}.endDate`, true)
                      }
                    />
                    <ErrorContainer
                      visible={
                        formik.touched.billing?.orderingProviders?.[index]?.endDate &&
                        errors?.endDate
                      }
                      errors={formik.errors}
                      name={`billing.orderingProviders.${index}.endDate`}
                    />
                  </Stack>
                  {!isTablet && (
                    <Stack flex={1}>
                      {index !== 0 && (
                        <button
                          onClick={() => remove(index)}
                          className={clsx(style.deleteIcon, 'deleteIcon')}
                        >
                          <span className={clsx('material-icons')}>delete</span>
                        </button>
                      )}
                    </Stack>
                  )}
                </Stack>
              );
            })}
            <Stack width="160px" mt="10px">
              <Button
                disabled={!hasBillingProviders}
                icon={<span className="material-icons">add</span>}
                theme="transparent"
                type="button"
                text="Add another"
                handleOnClick={() => push({ orderingProviderId: '', startDate: '', endDate: '' })}
                size="small"
              />
            </Stack>
          </>
        )}
      </FieldArray>
    </Stack>
  );
};

const BillingProgramSection = () => {
  const dispatch = useAppDispatch();
  const { isTablet } = useResponsive();
  const formik = useFormikContext<FormValues>();
  const { organizationId } = getLsUserRoleObject();
  const billingPrograms = useAppSelector((state) => state.clientProfile.billingPrograms.data);

  useEffect(() => {
    dispatch(getBillingPrograms({ organizationId }));
  }, [organizationId]);

  const hasBillingPrograms = billingPrograms.length > 0;

  return (
    <Stack width="100%" display="flex" flexDirection="column" gap="8px">
      <FieldArray name="billing.billingPrograms">
        {({ remove, push }) => (
          <>
            {formik.values?.billing?.billingPrograms?.map?.((billingProgram, index) => {
              const errors = formik.errors.billing?.billingPrograms?.[index];
              if (typeof errors === 'string') {
                return null;
              }
              return (
                <Stack
                  width="100%"
                  display="flex"
                  flexDirection={isTablet ? 'column' : 'row'}
                  alignItems={isTablet ? 'flex-start' : 'center'}
                  gap={isTablet ? '0px' : '24px'}
                  key={index}
                  className={style.rowWrapper}
                >
                  <Stack
                    flex={3}
                    width="100%"
                    display="flex"
                    flexDirection="row"
                    alignItems="center"
                    gap="4px"
                  >
                    <Stack flex={index === 0 ? undefined : 9} width={index === 0 ? '100%' : 'auto'}>
                      <LabelComponent text="Billing Program" />
                      <Dropdown
                        name={`billing.billingPrograms.${index}.billingProgramId`}
                        value={billingProgram.billingProgramId?.toString()}
                        onChange={formik.handleChange}
                        data={billingPrograms}
                        label="Select"
                        disabled={!hasBillingPrograms}
                      />
                      <ErrorContainer
                        visible={
                          formik.touched.billing?.billingPrograms?.[index]?.billingProgramId &&
                          errors?.billingProgramId
                        }
                        errors={formik.errors}
                        name={`billing.billingPrograms.${index}.billingProgramId`}
                      />
                    </Stack>
                    {isTablet && (
                      <Stack flex={1}>
                        {index !== 0 && (
                          <button onClick={() => remove(index)}>
                            <span className={clsx('material-icons', style.deleteIcon)}>delete</span>
                          </button>
                        )}
                      </Stack>
                    )}
                  </Stack>
                  <Stack flex={3} width="100%">
                    <LabelComponent text="Start Date" />
                    <DatePicker
                      disabled={!hasBillingPrograms}
                      value={billingProgram.startDate}
                      onChange={(value: string) => {
                        console.log({ value });
                        formik
                          .setFieldValue(`billing.billingPrograms.${index}.startDate`, value)
                          .then(() => {
                            formik.setFieldTouched(
                              `billing.billingPrograms.${index}.startDate`,
                              true
                            );
                          });
                      }}
                      onBlur={() =>
                        formik.setFieldTouched(`billing.billingPrograms.${index}.startDate`, true)
                      }
                    />
                    <ErrorContainer
                      visible={
                        formik.touched.billing?.billingPrograms?.[index]?.startDate &&
                        errors?.startDate
                      }
                      errors={formik.errors}
                      name={`billing.billingPrograms.${index}.startDate`}
                    />
                  </Stack>
                  <Stack flex={3} width="100%">
                    <LabelComponent text="End Date" />
                    <DatePicker
                      disabled={!hasBillingPrograms}
                      value={billingProgram.endDate}
                      onChange={(value: string) => {
                        formik
                          .setFieldValue(`billing.billingPrograms.${index}.endDate`, value)
                          .then(() => {
                            formik.setFieldTouched(
                              `billing.billingPrograms.${index}.endDate`,
                              true
                            );
                          });
                      }}
                      onBlur={() =>
                        formik.setFieldTouched(`billing.billingPrograms.${index}.endDate`, true)
                      }
                    />
                    <ErrorContainer
                      visible={
                        formik.touched.billing?.billingPrograms?.[index]?.endDate && errors?.endDate
                      }
                      errors={formik.errors}
                      name={`billing.billingPrograms.${index}.endDate`}
                    />
                  </Stack>
                  {!isTablet && (
                    <Stack flex={1}>
                      {index !== 0 && (
                        <button
                          onClick={() => remove(index)}
                          className={clsx(style.deleteIcon, 'deleteIcon')}
                        >
                          <span className={clsx('material-icons')}>delete</span>
                        </button>
                      )}
                    </Stack>
                  )}
                </Stack>
              );
            })}
            <Stack width="160px" mt="10px">
              <Button
                disabled={!hasBillingPrograms}
                icon={<span className="material-icons">add</span>}
                theme="transparent"
                type="button"
                text="Add another"
                handleOnClick={() => push({ billingProgramId: '', startDate: '', endDate: '' })}
                size="small"
              />
            </Stack>
          </>
        )}
      </FieldArray>
    </Stack>
  );
};

const ZCodesSection = () => {
  const formik = useFormikContext<FormValues>();

  return (
    <Stack>
      <FieldArray name="billing.zCodes">
        {({ remove, push }) => (
          <Stack>
            {formik.values?.billing?.zCodes?.map?.((zCode, index) => (
              <Stack
                key={index}
                display="flex"
                flexDirection="row"
                alignItems="center"
                className={style.rowWrapper}
              >
                <Stack flex={8}>
                  <ZCodesField
                    zCode={{ id: zCode.id, name: `${zCode.code} - ${zCode.description}` }}
                    index={index}
                  />
                </Stack>
                <Stack flex={1}>
                  {index !== 0 && (
                    <button onClick={() => remove(index)}>
                      <span className={clsx('material-icons', style.deleteIcon)}>delete</span>
                    </button>
                  )}
                </Stack>
              </Stack>
            ))}
            <Stack width="160px" mt="10px">
              <Button
                icon={<span className="material-icons">add</span>}
                theme="transparent"
                type="button"
                text="Add another"
                handleOnClick={() => push({ id: '', code: '', description: '' })}
                size="small"
              />
            </Stack>
          </Stack>
        )}
      </FieldArray>
    </Stack>
  );
};

const BillingSitesSection = () => {
  const { isTablet } = useResponsive();
  const formik = useFormikContext<FormValues>();
  const billingSites = useAppSelector((state) => state.clientProfile.sites.data);

  const hasBillingSites = billingSites.length > 0;

  return (
    <Stack width="100%" display="flex" flexDirection="column" gap="8px">
      <FieldArray name="billing.billingSites">
        {({ remove, push }) => {
          return (
            <>
              {formik.values?.billing?.billingSites?.map?.((billingSite, index) => {
                const errors = formik.errors.billing?.billingSites?.[index];
                if (typeof errors === 'string') {
                  return null;
                }
                return (
                  <Stack
                    width="100%"
                    display="flex"
                    flexDirection={isTablet ? 'column' : 'row'}
                    alignItems={isTablet ? 'flex-start' : 'center'}
                    gap={isTablet ? '0px' : '24px'}
                    key={index}
                    className={style.rowWrapper}
                  >
                    <Stack
                      flex={3}
                      width="100%"
                      display="flex"
                      flexDirection="row"
                      alignItems="center"
                      gap="4px"
                    >
                      <Stack
                        flex={index === 0 ? undefined : 9}
                        width={index === 0 ? '100%' : 'auto'}
                      >
                        <LabelComponent text="Billing Site" />
                        <Dropdown
                          name={`billing.billingSites.${index}.siteId`}
                          value={billingSite?.siteId?.toString()}
                          onChange={formik.handleChange}
                          data={billingSites.map((site) => ({
                            id: site.id?.toString(),
                            name: site.name,
                            category: site.active ? 'ACTIVE' : 'INACTIVE'
                          }))}
                          label="Select"
                          disabled={!hasBillingSites}
                          categoryList={[
                            { id: 'ACTIVE', label: 'Active' },
                            { id: 'INACTIVE', label: 'Inactive' }
                          ]}
                        />
                        <ErrorContainer
                          visible={
                            formik.touched.billing?.billingSites?.[index]?.siteId && errors?.siteId
                          }
                          errors={formik.errors}
                          name={`billing.billingSites.${index}.siteId`}
                        />
                      </Stack>
                      {isTablet && (
                        <Stack flex={1}>
                          {index !== 0 && (
                            <button onClick={() => remove(index)}>
                              <span className={clsx('material-icons', style.deleteIcon)}>
                                delete
                              </span>
                            </button>
                          )}
                        </Stack>
                      )}
                    </Stack>
                    <Stack flex={3} width="100%">
                      <LabelComponent text="Start Date" />
                      <DatePicker
                        disabled={!hasBillingSites}
                        value={billingSite.startDate}
                        onChange={(value: string) => {
                          formik
                            .setFieldValue(`billing.billingSites.${index}.startDate`, value)
                            .then(() => {
                              formik.setFieldTouched(
                                `billing.billingSites.${index}.startDate`,
                                true
                              );
                            });
                        }}
                        onBlur={() =>
                          formik.setFieldTouched(`billing.billingSites.${index}.startDate`, true)
                        }
                      />
                      <ErrorContainer
                        visible={
                          formik.touched.billing?.billingSites?.[index]?.startDate &&
                          errors?.startDate
                        }
                        errors={formik.errors}
                        name={`billing.billingSites.${index}.startDate`}
                      />
                    </Stack>
                    <Stack flex={3} width="100%">
                      <LabelComponent text="End Date" />
                      <DatePicker
                        disabled={!hasBillingSites}
                        value={billingSite.endDate}
                        onChange={(value: string) => {
                          formik
                            .setFieldValue(`billing.billingSites.${index}.endDate`, value)
                            .then(() => {
                              formik.setFieldTouched(`billing.billingSites.${index}.endDate`, true);
                            });
                        }}
                        onBlur={() =>
                          formik.setFieldTouched(`billing.billingSites.${index}.endDate`, true)
                        }
                      />
                      <ErrorContainer
                        visible={
                          formik.touched.billing?.billingSites?.[index]?.endDate && errors?.endDate
                        }
                        errors={formik.errors}
                        name={`billing.billingSites.${index}.endDate`}
                      />
                    </Stack>
                    {!isTablet && (
                      <Stack flex={1}>
                        {index !== 0 && (
                          <button
                            onClick={() => remove(index)}
                            className={clsx(style.deleteIcon, 'deleteIcon')}
                          >
                            <span className={clsx('material-icons')}>delete</span>
                          </button>
                        )}
                      </Stack>
                    )}
                  </Stack>
                );
              })}
              <Stack width="160px" mt="10px">
                <Button
                  disabled={!hasBillingSites}
                  icon={<span className="material-icons">add</span>}
                  theme="transparent"
                  type="button"
                  text="Add another"
                  handleOnClick={() => push({ siteId: '', startDate: '', endDate: '' })}
                  size="small"
                />
              </Stack>
            </>
          );
        }}
      </FieldArray>
    </Stack>
  );
};

const Billing = () => {
  const formik = useFormikContext<FormValues>();
  const dispatch = useAppDispatch();
  const { organizationId } = getLsUserRoleObject();

  useEffect(() => {
    dispatch(getBillingProviders({ organizationId }));
  }, [organizationId]);

  return (
    <AccordionComponent
      accordionTabId="billing"
      title="Billing"
      showWarning={isSectionInvalid(formik?.errors.billing, ['billingPrograms'])}
      description="This section is for billers and coordinators only."
    >
      <Stack mt="24px">
        <Stack display="flex" flexDirection="column" gap="36px">
          <Stack display="flex" flexDirection="column" gap="16px">
            <SectionTitle>Billing Program</SectionTitle>
            <BillingProgramSection />
          </Stack>
          <Stack display="flex" flexDirection="column" gap="16px">
            <SectionTitle>Ordering Provider</SectionTitle>
            <OrderingProviderSection />
          </Stack>
          <Stack display="flex" flexDirection="column" gap="16px">
            <SectionTitle>Billing Site</SectionTitle>
            <BillingSitesSection />
          </Stack>
        </Stack>
        <Stack mt="24px">
          <LabelComponent text="Primary Medical Diagnosis" />
          <InputField
            name="billing.primaryMedicalDiagnosis"
            value={formik.values.billing?.primaryMedicalDiagnosis}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder="Enter"
          />
          <ErrorContainer
            visible={
              formik.touched.billing?.primaryMedicalDiagnosis &&
              formik.errors.billing?.primaryMedicalDiagnosis
            }
            errors={formik.errors}
            name="billing.primaryMedicalDiagnosis"
          />
        </Stack>
        <Stack>
          <Branch label="Acuity">
            <Dropdown
              name="billing.acuity"
              value={formik.values.billing?.acuity}
              onChange={formik.handleChange}
              label="Select"
              data={[
                {
                  id: 'ACUTE',
                  name: 'Acute'
                },
                {
                  id: 'CHRONIC',
                  name: 'Chronic'
                }
              ]}
            />
          </Branch>
        </Stack>
        <Stack mt="36px" display="flex" flexDirection="column" gap="36px">
          <Stack display="flex" flexDirection="column" gap="16px">
            <SectionTitle>Z Codes</SectionTitle>
            <ZCodesSection />
          </Stack>
        </Stack>
      </Stack>
    </AccordionComponent>
  );
};

export default Billing;
